import { type ClientAccountsResp, type Platforms } from 'shared/api/accounts'
import {
  getDatasourceOptionId,
  mapAlvieAccountToFlowEntity,
  parseDatasourceOptionId
} from 'shared/copilot/utils/mapAlvieAccountToFlowEntity'
import { isSupportedFlowDatasourcePlatform, type FlowDatasourceEntity } from 'shared/copilot/flowDataAgentConstants'

/** Alvie client account presented as a selectable datasource option. */
export interface AlvieDatasourceOption {
  id: string
  label: string
  subLabel: string
  platform: Platforms
  connected: boolean
  /** Whether the Flow data agent can use this platform today. */
  supported: boolean
  /** Connected, but missing platform metadata the agent needs to query it. */
  needsSetup: boolean
  account: ClientAccountsResp
}

/**
 * Google Ads needs the manager (MCC) account id on every API call, and Alvie is
 * the only place it is resolved. An account whose metadata sync has not filled
 * it in yet cannot be queried, so say so at selection time rather than letting
 * the chat fail on the first question. Reconnecting the data source fills it in.
 */
const needsAccountSetup = (account: ClientAccountsResp): boolean =>
  account.platform === 'google' && (account.loginCustomerId ?? '') === ''

export const buildAlvieDatasourceOptions = (accounts: ClientAccountsResp[]): AlvieDatasourceOption[] => {
  return accounts
    .map((account) => ({
      id: getDatasourceOptionId(account),
      label: account.name,
      subLabel: account.externalAccountId,
      platform: account.platform,
      connected: account.connected,
      supported: isSupportedFlowDatasourcePlatform(account.platform),
      needsSetup: needsAccountSetup(account),
      account
    }))
    .sort((left, right) => {
      // Supported platforms first, then connected, then label.
      if (left.supported !== right.supported) {
        return left.supported ? -1 : 1
      }
      if (left.connected !== right.connected) {
        return left.connected ? -1 : 1
      }
      return left.label.localeCompare(right.label)
    })
}

export const resolveFlowDatasourceEntity = (
  optionId: string | undefined,
  datasourceOptions: AlvieDatasourceOption[]
): FlowDatasourceEntity | null => {
  if (optionId == null || parseDatasourceOptionId(optionId) == null) {
    return null
  }

  const option = datasourceOptions.find((candidate) => candidate.id === optionId)
  // needsSetup accounts are disabled in the picker. Reject them here too so a
  // restored selection is not mapped into agent state and refused on the first query.
  if (option == null || !isSupportedFlowDatasourcePlatform(option.platform) || option.needsSetup) {
    return null
  }

  return mapAlvieAccountToFlowEntity(option.account, option.platform)
}

export const resolveFlowDatasourceEntities = (
  optionIds: readonly string[],
  datasourceOptions: AlvieDatasourceOption[]
): FlowDatasourceEntity[] => {
  return optionIds.flatMap((optionId) => {
    const entity = resolveFlowDatasourceEntity(optionId, datasourceOptions)
    return entity == null ? [] : [entity]
  })
}

/**
 * Copilot currently supports a single datasource entity at a time.
 * Keeps the first supported option the data agent can query.
 * Accounts that still need setup are skipped so a restored selection
 * cannot stay active after the picker has disabled them.
 */
export const filterToSingleDatasource = (
  optionIds: readonly string[],
  datasourceOptions: AlvieDatasourceOption[]
): string[] => {
  for (const optionId of optionIds) {
    const option = datasourceOptions.find((candidate) => candidate.id === optionId)
    if (option?.supported === true && !option.needsSetup) {
      return [optionId]
    }
  }
  return []
}
